'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { ColorInputWrapper, ColorSwatch, ColorTextInput } from './styled';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const DEBOUNCE_MS = 80;

interface WlasColorInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

export const WlasColorInput: FC<WlasColorInputProps> = ({ value, onChange, label = 'Color' }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const displayValue = draft ?? value;
  const safeHex = HEX_RE.test(value) ? value : '#000000';

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleSwatchChange = (raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange(raw);
    }, DEBOUNCE_MS);
  };

  const handleTextChange = (raw: string) => {
    setDraft(raw);
    if (HEX_RE.test(raw)) {
      onChange(raw);
      setDraft(null);
    }
  };

  const handleTextBlur = () => {
    setDraft(null);
  };

  return (
    <ColorInputWrapper>
      <ColorSwatch
        type="color"
        value={safeHex}
        onChange={(e) => handleSwatchChange(e.target.value)}
        aria-label={label}
      />
      <ColorTextInput
        type="text"
        value={displayValue}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleTextBlur}
        maxLength={7}
        placeholder="#000000"
        aria-label={label}
      />
    </ColorInputWrapper>
  );
};
